import CloseIcon from "@mui/icons-material/Close";
import { Grid, IconButton, MenuItem, TextField } from "@mui/material";
import { useTranslation } from "common/locales";
import { FormikErrors } from "formik";
import { SOLD_OR_KEPT } from "model";
import { YieldDetailsFormData } from "./EnterYieldEvent";

import { useEffect } from "react";

interface Props {
  index: number;
  yieldDetail: YieldDetailsFormData;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleInputChange: (event: any, name: string) => void;
  errors?: FormikErrors<YieldDetailsFormData>;
  remove: (index: number) => void;
  possibleYieldTypeValues: { [key: string]: string };
  yieldTypeTPrefix: string;
  possibleUnitTypeValues: { [key: string]: string };
  unitTypeTPrefix: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

export default function EnterYieldEventYieldDetails({
  index,
  yieldDetail,
  handleInputChange,
  errors,
  remove,
  possibleYieldTypeValues,
  yieldTypeTPrefix,
  possibleUnitTypeValues,
  unitTypeTPrefix,
  setFieldValue,
}: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    if (yieldDetail.detailYieldType) {
      if (!(yieldDetail.detailYieldType in possibleYieldTypeValues)) {
        setFieldValue(`yieldAmounts[${index}].detailYieldType`, "", true);
      }
    }
  }, [
    possibleYieldTypeValues,
    index,
    setFieldValue,
    yieldDetail.detailYieldType,
  ]);

  const showRevenue = yieldDetail.soldOrKept === SOLD_OR_KEPT.SOLD;

  return (
    <Grid
      container
      spacing={1.5}
      sx={{
        mb: 2,
        pb: index > 0 ? 1.5 : 0,
        borderBottom: index > 0 ? "1px solid rgba(0,0,0,0.08)" : "none",
      }}
      alignItems="center"
    >
      <Grid size={{ xs: 6, sm: showRevenue ? 2 : 2.5 }}>
        <TextField
          select
          fullWidth
          required
          size="small"
          value={yieldDetail.detailYieldType ?? ""}
          onChange={(event) => {
            handleInputChange(event, `yieldAmounts[${index}].detailYieldType`);
          }}
          error={
            !!(
              errors?.detailYieldType ||
              (yieldDetail.detailYieldType &&
                !(yieldDetail.detailYieldType in possibleYieldTypeValues))
            )
          }
          label={t("yieldEvent.yieldType.text")}
          name={`yieldAmounts[${index}].detailYieldType`}
        >
          {Object.entries(possibleYieldTypeValues)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {t(`${yieldTypeTPrefix}.${value}`)}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 6, sm: showRevenue ? 2 : 2.5 }}>
        <TextField
          select
          fullWidth
          required
          size="small"
          value={yieldDetail.unitType ?? ""}
          onChange={(event) => {
            handleInputChange(event, `yieldAmounts[${index}].unitType`);
          }}
          error={!!errors?.unitType}
          label={t("yieldEvent.unitType.text")}
          name={`yieldAmounts[${index}].unitType`}
        >
          {Object.entries(possibleUnitTypeValues)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {t(`${unitTypeTPrefix}.${value}`)}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 6, sm: showRevenue ? 2 : 2.5 }}>
        <TextField
          select
          fullWidth
          required
          size="small"
          value={yieldDetail.soldOrKept ?? ""}
          onChange={(event) => {
            handleInputChange(event, `yieldAmounts[${index}].soldOrKept`);
          }}
          error={!!errors?.soldOrKept}
          label={t("yieldEvent.soldOrKept.text")}
          name={`yieldAmounts[${index}].soldOrKept`}
        >
          {Object.entries(SOLD_OR_KEPT)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {t(`yieldEvent.soldOrKept.label.${value}`)}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 6, sm: showRevenue ? 2 : 2.5 }}>
        <TextField
          required
          fullWidth
          size="small"
          value={yieldDetail.amount}
          type="number"
          id={`amount-${index}`}
          error={!!errors?.amount}
          onChange={(event) => {
            handleInputChange(event, `yieldAmounts[${index}].amount`);
          }}
          label={t("yieldEvent.yieldAmount.label")}
          name={`yieldAmounts[${index}].amount`}
        />
      </Grid>
      {showRevenue && (
        <Grid size={{ xs: 6, sm: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={yieldDetail.revenue}
            type="number"
            id={`revenue-${index}`}
            error={!!errors?.revenue}
            onChange={(event) => {
              handleInputChange(event, `yieldAmounts[${index}].revenue`);
            }}
            label={t("yieldEvent.revenue.label")}
            name={`yieldAmounts[${index}].revenue`}
          />
        </Grid>
      )}
      {index > 0 && (
        <Grid size="auto" sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            size="small"
            onClick={() => remove(index)}
            sx={{ color: "rgba(0,0,0,0.4)" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
      )}
    </Grid>
  );
}
